/** Pick customers of one app by search, or paste a list of customer IDs. */
import { LIMITS } from "@aiyo/shared";
import { Plus } from "@gravity-ui/icons";
import { Button, ListBox, Tag, TagGroup } from "@heroui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounced } from "../lib/hooks";
import { useCustomers } from "../lib/queries";
import { TextAreaInput, TextInput } from "./fields";
import { ChannelChip } from "./ui";

const split = (text: string) =>
  text
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);

export function CustomerPicker({
  appId,
  value,
  onChange,
  error,
}: {
  appId: string;
  value: string[];
  onChange: (externalIds: string[]) => void;
  error?: string | null;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [pasteOpen, setPasteOpen] = useState(false);
  const [paste, setPaste] = useState("");
  const term = useDebounced(q.trim());
  const results = useCustomers({ app_id: appId, q: term || undefined });
  const matches = (results.data?.pages[0]?.items ?? [])
    .filter((c) => !value.includes(c.external_id))
    .slice(0, 6);
  const add = (ids: string[]) =>
    onChange([...new Set([...value, ...ids])].slice(0, LIMITS.maxRecipientsPerRequest));

  return (
    <div className="flex flex-col gap-3">
      <TextInput
        name="customer-search"
        label={t("send.customers.search")}
        placeholder={t("send.customers.searchPlaceholder")}
        value={q}
        onChange={setQ}
        error={error}
      />
      {matches.length ? (
        <div className="overflow-hidden rounded-xl border border-border">
          <ListBox
            aria-label={t("send.customers.results")}
            selectionMode="none"
            onAction={(key) => add([String(key)])}
          >
            {matches.map((c) => (
              <ListBox.Item key={c.id} id={c.external_id} textValue={c.name ?? c.external_id}>
                <div className="flex w-full items-center gap-3 py-0.5">
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm font-medium">{c.name ?? c.external_id}</span>
                    <span className="truncate font-mono text-xs text-muted">{c.external_id}</span>
                  </span>
                  <span className="hidden flex-wrap justify-end gap-1 sm:flex">
                    {c.channels
                      .filter((ch) => ch.active > 0)
                      .map((ch) => (
                        <ChannelChip key={ch.channel} channel={ch.channel} />
                      ))}
                  </span>
                  <Plus className="size-4 shrink-0 text-muted" aria-hidden />
                </div>
              </ListBox.Item>
            ))}
          </ListBox>
        </div>
      ) : term && !results.isFetching ? (
        <p className="text-xs text-muted">{t("send.customers.noMatch")}</p>
      ) : null}

      {value.length ? (
        <TagGroup
          aria-label={t("send.customers.selectedLabel")}
          onRemove={(keys) => onChange(value.filter((id) => !keys.has(id)))}
        >
          <TagGroup.List items={value.map((id) => ({ id }))}>
            {(item) => (
              <Tag id={item.id} textValue={item.id}>
                <span className="font-mono text-xs">{item.id}</span>
              </Tag>
            )}
          </TagGroup.List>
        </TagGroup>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-muted">{t("send.customers.selected", { count: value.length })}</span>
        <div className="flex gap-1">
          {value.length ? (
            <Button size="sm" variant="ghost" onPress={() => onChange([])}>
              {t("send.customers.clear")}
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" onPress={() => setPasteOpen((v) => !v)}>
            {t("send.customers.paste")}
          </Button>
        </div>
      </div>
      {pasteOpen ? (
        <div className="flex flex-col gap-2">
          <TextAreaInput
            name="customer-ids"
            label={t("send.customers.pasteLabel")}
            description={t("send.customers.pasteHint")}
            placeholder="user-123"
            value={paste}
            onChange={setPaste}
            rows={3}
            mono
          />
          <Button
            size="sm"
            variant="secondary"
            className="self-start"
            isDisabled={!split(paste).length}
            onPress={() => {
              add(split(paste));
              setPaste("");
              setPasteOpen(false);
            }}
          >
            {t("send.customers.addPasted", { count: split(paste).length })}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
